import React from 'react';
import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

export interface PerspectiveInfo {
  personaUserId: string;
  personaName: string;
  ideology: string;
  articleId: string;
  color: string;
  avatarUrl: string | null;
  spectrumPosition: number;
}

export interface PerspectiveTogglePayload {
  newsEventGroupId: string;
  perspectives: PerspectiveInfo[];
  spectrumLeftLabel: string;
  spectrumRightLabel: string;
  activePersonaUserId: string;
}

export type SerializedPerspectiveToggleNode = Spread<
  PerspectiveTogglePayload,
  SerializedLexicalNode
>;

function PerspectiveToggleComponent({
  perspectives,
  spectrumLeftLabel,
  spectrumRightLabel,
  activePersonaUserId,
}: PerspectiveTogglePayload & { nodeKey: string }) {
  return (
    <div
      style={{
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '20px 24px',
        margin: '0 0 32px 0',
      }}
    >
      {/* Persona pills */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {perspectives.map((p) => {
          const isActive = p.personaUserId === activePersonaUserId;
          return (
            <div
              key={p.personaUserId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 14px',
                borderRadius: '24px',
                border: `2px solid ${isActive ? p.color : '#e2e8f0'}`,
                backgroundColor: isActive ? `${p.color}10` : 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: p.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                {p.personaName.split(' ').map((n) => n[0]).join('')}
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: isActive ? 700 : 500, color: '#1e293b' }}>
                  {p.personaName}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>{p.ideology}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Spectrum bar */}
      <div style={{ padding: '0 4px' }}>
        <div
          style={{
            position: 'relative',
            height: '4px',
            backgroundColor: '#e2e8f0',
            borderRadius: '2px',
          }}
        >
          {perspectives.map((p) => (
            <div
              key={p.personaUserId}
              style={{
                position: 'absolute',
                left: `${p.spectrumPosition}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: p.personaUserId === activePersonaUserId ? '14px' : '10px',
                height: p.personaUserId === activePersonaUserId ? '14px' : '10px',
                borderRadius: '50%',
                backgroundColor: p.color,
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                transition: 'all 0.2s',
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>{spectrumLeftLabel}</span>
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>{spectrumRightLabel}</span>
        </div>
      </div>
    </div>
  );
}

export class PerspectiveToggleNode extends DecoratorNode<React.ReactElement> {
  __newsEventGroupId: string;
  __perspectives: PerspectiveInfo[];
  __spectrumLeftLabel: string;
  __spectrumRightLabel: string;
  __activePersonaUserId: string;

  static getType(): string {
    return 'perspective-toggle';
  }

  static clone(node: PerspectiveToggleNode): PerspectiveToggleNode {
    return new PerspectiveToggleNode(
      node.__newsEventGroupId,
      node.__perspectives,
      node.__spectrumLeftLabel,
      node.__spectrumRightLabel,
      node.__activePersonaUserId,
      node.__key,
    );
  }

  constructor(
    newsEventGroupId: string,
    perspectives: PerspectiveInfo[],
    spectrumLeftLabel: string,
    spectrumRightLabel: string,
    activePersonaUserId: string,
    key?: NodeKey,
  ) {
    super(key);
    this.__newsEventGroupId = newsEventGroupId;
    this.__perspectives = perspectives;
    this.__spectrumLeftLabel = spectrumLeftLabel;
    this.__spectrumRightLabel = spectrumRightLabel;
    this.__activePersonaUserId = activePersonaUserId;
  }

  static importJSON(serialized: SerializedPerspectiveToggleNode): PerspectiveToggleNode {
    return new PerspectiveToggleNode(
      serialized.newsEventGroupId,
      serialized.perspectives,
      serialized.spectrumLeftLabel,
      serialized.spectrumRightLabel,
      serialized.activePersonaUserId,
    );
  }

  exportJSON(): SerializedPerspectiveToggleNode {
    return {
      type: 'perspective-toggle',
      version: 1,
      newsEventGroupId: this.__newsEventGroupId,
      perspectives: this.__perspectives,
      spectrumLeftLabel: this.__spectrumLeftLabel,
      spectrumRightLabel: this.__spectrumRightLabel,
      activePersonaUserId: this.__activePersonaUserId,
    };
  }

  createDOM(): HTMLElement {
    const div = document.createElement('div');
    div.className = 'perspective-toggle-node';
    return div;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): React.ReactElement {
    return (
      <PerspectiveToggleComponent
        newsEventGroupId={this.__newsEventGroupId}
        perspectives={this.__perspectives}
        spectrumLeftLabel={this.__spectrumLeftLabel}
        spectrumRightLabel={this.__spectrumRightLabel}
        activePersonaUserId={this.__activePersonaUserId}
        nodeKey={this.__key}
      />
    );
  }
}

export function $createPerspectiveToggleNode(payload: PerspectiveTogglePayload): PerspectiveToggleNode {
  return new PerspectiveToggleNode(
    payload.newsEventGroupId,
    payload.perspectives,
    payload.spectrumLeftLabel,
    payload.spectrumRightLabel,
    payload.activePersonaUserId,
  );
}

export function $isPerspectiveToggleNode(node: LexicalNode): node is PerspectiveToggleNode {
  return node instanceof PerspectiveToggleNode;
}
